/**
 * MultiplayerGame — multiplayer game view.
 *
 * Full-bleed graph with cooldown-based simultaneous play.
 * All players interact with the graph at the same time,
 * with a per-player cooldown between actions.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import WordGraph from './WordGraph';
import MultiplayerHUD from './MultiplayerHUD';
import MultiplayerVictory from './MultiplayerVictory';
import GraphSearch from './GraphSearch';
import HowToPlay from './HowToPlay';
import BombIcon from './BombIcon';
import useSound from '../hooks/useSound';
import { findShortestPath, pathToEdgeSet } from '../utils/graph';

export default function MultiplayerGame({ multiplayer }) {
  const {
    nodes, links,
    selectedWord, lastExpandedWord, expansionFocusIds, lastCombineResult,
    targetWord, initialWord,
    players, isHost,
    onCooldown, cooldownEnd, cooldownDuration,
    bombs,
    gameComplete, victoryPending, winningWord,
    winnerId, winnerName,
    loading, error,
    handleNodeClick, combineWords,
    useBomb, returnToLobby,
    mySocketId,
    connected,
  } = multiplayer;

  const sound = useSound();

  const [showHow, setShowHow] = useState(false);
  const [searchedWord, setSearchedWord] = useState(null);
  const [bombMode, setBombMode] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [showVictoryModal, setShowVictoryModal] = useState(true);

  const hasNodes = nodes.length > 0;
  const myBombs = bombs[mySocketId] || 0;
  const showPostGameControls = gameComplete && !showVictoryModal;

  const handleViewGraph = useCallback(() => {
    setShowVictoryModal(false);
  }, []);

  // Reset modal visibility when a new game starts
  useEffect(() => {
    if (!gameComplete) setShowVictoryModal(true);
  }, [gameComplete]);

  // Compute shortest path edges when game is complete and modal is dismissed
  const shortestPathEdges = useMemo(() => {
    if (!gameComplete || showVictoryModal) return null;
    if (!initialWord || !targetWord) return null;
    const path = findShortestPath(initialWord, targetWord, links);
    return path ? pathToEdgeSet(path) : null;
  }, [gameComplete, showVictoryModal, initialWord, targetWord, links]);

  // Wrap node click — in bomb mode, bomb the node instead of expanding
  const onNodeClick = useCallback((word) => {
    if (bombMode && myBombs > 0) {
      useBomb(word);
      setBombMode(false);
      return;
    }
    handleNodeClick(word);
    sound.playExpand();
  }, [bombMode, myBombs, useBomb, handleNodeClick, sound]);

  // Wrap combine with sound
  const onCombine = useCallback((w1, w2) => {
    combineWords(w1, w2);
    sound.playCombine();
  }, [combineWords, sound]);

  // Play victory sound
  useEffect(() => {
    if (victoryPending) sound.playVictory();
  }, [victoryPending, sound]);

  // Clear search highlight on any graph interaction (expand/combine)
  useEffect(() => { setSearchedWord(null); }, [expansionFocusIds]);

  const handleSearch = useCallback((word) => {
    setSearchedWord(word || null);
  }, []);

  // Cooldown countdown timer
  useEffect(() => {
    if (!onCooldown || !cooldownEnd) {
      setCooldownRemaining(0);
      return;
    }
    const tick = () => {
      const remaining = Math.max(0, cooldownEnd - Date.now());
      setCooldownRemaining(remaining);
    };
    tick();
    const interval = setInterval(tick, 50);
    return () => clearInterval(interval);
  }, [onCooldown, cooldownEnd]);

  const cooldownSeconds = (cooldownRemaining / 1000).toFixed(1);

  return (
    <div className="app">
      {/* ── Full-bleed graph ── */}
      <div className="graph-viewport">
        {error && (
          <div className="toast-error">{error}</div>
        )}

        {!hasNodes && (
          <div className="loading-screen">
            <div className="loading-orbit" />
            <div className="loading-label">Starting game...</div>
          </div>
        )}

        {hasNodes && (
          <WordGraph
            nodes={nodes}
            links={links}
            onNodeClick={onNodeClick}
            onCombine={onCombine}
            selectedWord={selectedWord}
            targetWord={targetWord}
            winningWord={winningWord}
            victoryPending={victoryPending}
            initialWord={initialWord}
            lastExpandedWord={lastExpandedWord}
            expansionFocusIds={expansionFocusIds}
            lastCombineResult={lastCombineResult}
            searchedWord={searchedWord}
            loading={loading}
            disabled={onCooldown && !bombMode}
            bombMode={bombMode}
            shortestPathEdges={shortestPathEdges}
          />
        )}
      </div>

      {/* ── Header bar ── */}
      <header className="hud-header">
        <div className="hud-logo">
          <span className="logo-l">L</span><span className="logo-e">e</span><span className="logo-x">x</span><span className="logo-i">i</span><span className="logo-quest">Quest</span>
          <button className="help-trigger" onClick={() => setShowHow(true)} aria-label="How to play">?</button>
          <span
            className={`connection-dot ${connected ? 'connected' : 'disconnected'}`}
            title={connected ? 'Connected' : 'Reconnecting...'}
          />
          {!connected && <span className="connection-label">Reconnecting...</span>}
        </div>

        {targetWord && (
          <div className="target-beacon">
            <span className="target-label">find</span>
            <span className="target-word">{(targetWord || '').replace(/\b\w/g, c => c.toUpperCase())}</span>
          </div>
        )}

        <div className="hud-header-right">
          {hasNodes && <GraphSearch nodes={nodes} onSearch={handleSearch} />}
          <div className={`hud-stats mp-stats ${onCooldown ? '' : 'mp-ready'}`}>
            <div className="stat-group mp-cooldown-group">
              {onCooldown ? (
                <>
                  <div className="mp-cooldown-bar-track">
                    <div
                      className="mp-cooldown-bar-fill"
                      style={{ width: `${(cooldownRemaining / (cooldownDuration * 1000)) * 100}%` }}
                    />
                  </div>
                  <span className="mp-cooldown-label">{cooldownSeconds}s</span>
                </>
              ) : (
                <>
                  <span className="mp-ready-dot" />
                  <span className="mp-ready-label">Ready</span>
                </>
              )}
            </div>
            {myBombs > 0 && !gameComplete && (
              <>
                <div className="stat-sep" />
                <button
                  className={`btn-bomb ${bombMode ? 'bomb-active' : ''}`}
                  onClick={() => setBombMode(prev => !prev)}
                >
                  <BombIcon size={14} /> {myBombs}
                </button>
              </>
            )}
            <button
              className={`btn-sound ${sound.muted ? 'muted' : ''}`}
              onClick={sound.toggleMute}
              aria-label={sound.muted ? 'Unmute sounds' : 'Mute sounds'}
              title={sound.muted ? 'Enable sound' : 'Mute sound'}
            >
              {sound.muted ? '\u{1F507}' : '\u{1F50A}'}
            </button>
          </div>
        </div>
      </header>

      {showHow && <HowToPlay onClose={() => setShowHow(false)} variant="multiplayer" />}

      {/* Player list sidebar */}
      <MultiplayerHUD
        players={players}
        bombs={bombs}
      />

      {/* Bomb mode indicator */}
      {bombMode && (
        <div className="mp-bomb-banner">
          Click a node to bomb it — or press the bomb button again to cancel
        </div>
      )}

      {/* ── Post-game floating controls ── */}
      {showPostGameControls && (
        <div className="post-game-controls">
          {isHost && (
            <button className="btn-new" onClick={returnToLobby}>
              Return to Lobby
            </button>
          )}
          <button className="btn-new" onClick={() => setShowVictoryModal(true)}>
            View Results
          </button>
        </div>
      )}

      {/* ── Post-game legend ── */}
      {showPostGameControls && (
        <div className="hud-legend">
          <div className="legend-item"><span className="legend-swatch shortest-path" /><span>Shortest Path</span></div>
          <div className="legend-item"><span className="legend-swatch start" /><span>Start</span></div>
          <div className="legend-item"><span className="legend-swatch target" /><span>Target</span></div>
        </div>
      )}

      {/* ── Victory ── */}
      {gameComplete && showVictoryModal && (
        <MultiplayerVictory
          winnerName={winnerName}
          winnerId={winnerId}
          mySocketId={mySocketId}
          winningWord={winningWord}
          onReturnToLobby={returnToLobby}
          onViewGraph={handleViewGraph}
          isHost={isHost}
        />
      )}
    </div>
  );
}
